import { Service, DiscountCategory } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { NumberInput, Badge, Text, Group, Popover, Button, Loader } from '@mantine/core';
import { IconPercentage, IconEdit, IconExternalLink } from '@tabler/icons-react';
import { useState, useEffect, useMemo } from 'react';
import { useUpdateService, useDiscountCategories } from '@/hooks/api-hooks';
import { SkeletonList } from '../snippets';
import { notify } from '@/lib/notify';
import { ServiceTitleStatic } from '../services/service-title';
import { SERVICES_PATH } from '@/lib/navigation';
import { Link } from 'react-router-dom';

interface Props {
    services: Service[];
}

interface ServiceUpdateStatus {
    id: Service['id'];
    loading: boolean;
    success: boolean | null;
}

const DELAY = 350; // ms

export function BulkEditDiscountsTab({ services }: Props) {
    const { t } = useTranslation();
    const [discounts, setDiscounts] = useState<DiscountCategory[]>([]);
    const [editingCell, setEditingCell] = useState<{ category: string; currency: string } | null>(null);
    const [tempValue, setTempValue] = useState<number>(0);
    const [updateStatuses, setUpdateStatuses] = useState<Record<Service['id'], ServiceUpdateStatus>>({});
    const [counter, setCounter] = useState<number>(0);

    const updateServiceMutation = useUpdateService();
    const {
        data: discountCategories,
        isLoading: isLoadingDiscountCategories
    } = useDiscountCategories();

    const discountCategoriesData = useMemo(
        () => discountCategories?.data || [], [discountCategories]
    );

    const hasStarted = Object.keys(updateStatuses).length > 0;
    const allCompleted = hasStarted && Object.values(updateStatuses).every(status => !status.loading);

    useEffect(() => {
        // Initialize discounts with default values from discount categories
        setDiscounts(discountCategoriesData.map(cat => ({ ...cat })));
    }, [discountCategoriesData]);

    const handleDiscountChange = (categoryName: string, currency: string, percentage: number) => {
        const updatedDiscounts = discounts.map(discount =>
            discount.category === categoryName
                ? {
                    ...discount,
                    discounts: discount.discounts.map(d =>
                        d.currency === currency
                            ? { ...d, percentage }
                            : d
                    )
                }
                : discount
        );

        setDiscounts(updatedDiscounts);
    };

    const handleCellClick = (categoryName: string, currency: string) => {
        const discount = discounts
            ?.find(d => d.category === categoryName)
            ?.discounts.find(d => d.currency === currency);

        if (discount) {
            setEditingCell({ category: categoryName, currency });
            setTempValue(discount.percentage);
        }
    };

    const handleSave = () => {
        if (editingCell) {
            handleDiscountChange(editingCell.category, editingCell.currency, tempValue);
            setEditingCell(null);
        }
    };

    const handleCancel = () => {
        setEditingCell(null);
    };

    const getDiscountValue = (categoryName: string, currency: string) => {
        return discounts
            ?.find(d => d.category === categoryName)
            ?.discounts.find(d => d.currency === currency)?.percentage || 0;
    };

    const handleBulkUpdate = async () => {
        // Initialize statuses
        const initialStatuses: Record<Service['id'], ServiceUpdateStatus> = {};
        services.forEach(service => {
            initialStatuses[service.id] = { id: service.id, loading: true, success: null };
        });
        setUpdateStatuses(initialStatuses);
        setCounter(0);

        // Apply the same discount scheme to all services
        let completed = 0;
        for (let i = 0; i < services.length; i++) {
            const service = services[i];

            if (i > 0) {
                await new Promise(resolve => setTimeout(resolve, DELAY));
            }

            try {
                await updateServiceMutation.mutateAsync({
                    id: service.id,
                    discounts: discounts,
                });

                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [service.id]: { id: service.id, loading: false, success: true }
                }));

                if (completed === services.length) {
                    notify.success(t('discount_update_success'), '');
                }
            } catch (error) {
                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [service.id]: { id: service.id, loading: false, success: false }
                }));
                notify.error(t('discount_update_error'), '');
            }
        }
    };

    // Get unique currencies from all discount categories
    const currencies = Array.from(new Set(
        discountCategoriesData.flatMap(category => category.discounts.map(d => d.currency))
    )).sort();

    if (isLoadingDiscountCategories) {
        return <SkeletonList />
    }

    return (
        <>
            <div className="space-y-4">
                <div className="text-sm font-medium mb-2">
                    {t('set_discount_scheme_for_all_services')}
                </div>
                <div className="text-xs text-gray-500 mb-4">
                    {t('this_discount_will_be_applied_to')} {services.length} {t('services')}
                </div>

                <div className="subtle-table">
                    <table>
                        <thead>
                            <tr>
                                <th>
                                    {t('client_type')}
                                </th>
                                {currencies.map((currency) => (
                                    <th key={currency}>
                                        <Badge
                                            color="blue"
                                            variant="light"
                                            size="sm"
                                        >
                                            {currency}
                                        </Badge>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {discounts.map((category) => (
                                <tr key={category.category}>
                                    <td>
                                        <Badge variant="light" size="sm">
                                            {category.category}
                                        </Badge>
                                    </td>
                                    {currencies.map((currency) => (
                                        <td key={`${category.category}-${currency}`}>
                                            <Popover
                                                opened={editingCell?.category === category.category && editingCell?.currency === currency}
                                                onChange={() => setEditingCell(null)}
                                                position="bottom"
                                                withArrow
                                                shadow="md"
                                            >
                                                <Popover.Target>
                                                    <Button
                                                        variant="subtle"
                                                        size="sm"
                                                        onClick={() => handleCellClick(category.category, currency)}
                                                        className="w-full h-8"
                                                    >
                                                        <Group gap="xs" justify="center">
                                                            <Text size="sm">
                                                                {getDiscountValue(category.category, currency)}%
                                                            </Text>
                                                            <IconEdit size={14} />
                                                        </Group>
                                                    </Button>
                                                </Popover.Target>
                                                <Popover.Dropdown>
                                                    <div className="p-2 space-y-3">
                                                        <Text size="sm" fw={500}>
                                                            Edit {currency} discount for {category.category}
                                                        </Text>
                                                        <NumberInput
                                                            value={tempValue}
                                                            onChange={(value) => setTempValue(Number(value) || 0)}
                                                            min={0}
                                                            max={100}
                                                            rightSection={<IconPercentage size={16} />}
                                                            size="sm"
                                                            w={120}
                                                        />
                                                        <Group gap="xs" justify="flex-end">
                                                            <Button
                                                                size="xs"
                                                                variant="subtle"
                                                                onClick={handleCancel}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                size="xs"
                                                                onClick={handleSave}
                                                            >
                                                                Save
                                                            </Button>
                                                        </Group>
                                                    </div>
                                                </Popover.Dropdown>
                                            </Popover>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="space-y-4 solid-box-border-green text-xs max-h-96 overflow-y-auto mt-10">
                    {services.map(service => (
                        <Link
                            key={service.id}
                            to={`${SERVICES_PATH}/${service.id}?tab=discounts`}
                            target="_blank"
                            className="block text-xs flex items-start gap-1 mb-2"
                        >
                            <IconExternalLink size={16} />
                            <div className="flex-1">
                                {ServiceTitleStatic(service)}
                            </div>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="mt-8">
                {!hasStarted ? (
                    <div className="flex justify-end">
                        <Button
                            onClick={handleBulkUpdate}
                            color="black"
                            radius="xl"
                            size="xs"
                        >
                            {t('apply_changes')}
                        </Button>
                    </div>
                ) : allCompleted ? (
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-sm font-medium text-green-600">
                            {t('done')}
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-sm font-medium">
                            {t('updating')} {counter} / {services.length}
                        </div>
                        <Loader size="sm" color="black" />
                    </div>
                )}
            </div>
        </>
    );
}
